import { useEffect, useState } from "react";
import { useNavigate, useParams } from 'react-router-dom';

import useApi from "../../hooks/ApiHook";
import useToast from "../../hooks/ToastHook";

import { Button } from "primereact/button";
import { Sidebar } from 'primereact/sidebar';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { TabMenu } from 'primereact/tabmenu';

import PageContent from "../../components/PageContent";
import PageTitle from "../../components/PageTitle";
import GeneralInformation from "./components/GeneralInformation";
import ProductsList from "./components/ProductsList";
import ProductDetailsForm from "./components/ProductDetailsForm";
import ICMSForm from "./components/ICMSForm";
import BillingInformation from "./components/BillingInformation";

const NewSale = () => {
    const api = useApi();
    const { showSuccess, showError, showAxiosError } = useToast();
    let { id } = useParams();
    const navigate = useNavigate();

    const items = [
        { label: 'Geral' },
        { label: 'Produtos' },
        { label: 'Cobrança' },
    ];

    const [activeIndex, setActiveIndex] = useState(0);
    const [generalData, setGeneralData] = useState({
        destinyCompany: '',
        destinyState: 'AC'
    });
    const [billingData, setBillingData] = useState({ charge: { discount: 0 } });
    const [products, setProducts] = useState([]);
    const [saving, setSaving] = useState(false);
    const [selectedProductIndex, setSelectedProductIndex] = useState(null);
    const [productData, setProductData] = useState({});
    const [productICMSData, setProductICMSData] = useState({});
    const [settings, setSettings] = useState({});
    const [formStep, setFormStep] = useState('general');

    useEffect(() => {
        if (generalData.priceTable && products) {
            const tableItems = generalData.priceTable.items;
            const newProducts = products.map(p => {
                const price = tableItems.filter(i => i.product.id === p.product.id)[0]?.price;
                return { ...p, unitaryValue: (price || 0) }
            });
            setProducts(newProducts);
        }
    }, [generalData.priceTable]);

    const handleProductClick = (i, formStep) => {
        setFormStep(formStep);
        setSelectedProductIndex(i);
        setProductData(products[i]);
        setProductICMSData(products[i].icms || {});
    }

    const handleProductDetailsChange = (passToICMS) => {
        if (
            !productData.product || 
            !productData.quantity ||
            !productData.unitaryValue ||
            !productData.cfop ||
            !productData.ipi.cst ||
            !productData.ipi.legalFrameworkCode ||
            !productData.pis.cst ||
            !productData.cofins.cst
        ) {
            showError('Erro', 'Preencha todos os campos obrigatórios');
            return;
        }

        const updatedProducts = products.map((p, i) => {
            if (i === selectedProductIndex) {
                return {
                    ...p,
                    ...productData,
                };
            }

            return p;
        });

        setProducts(updatedProducts);

        if (passToICMS) {
            setFormStep('icms');
            return;
        }

        setProductData({});
        setSelectedProductIndex(null);
    }

    const handleProductICMSChange = (openNextProduct) => {
        const isSN = settings?.sn?.active;
        
        const isPrimaryDataCorrect = (() => {
            if (productICMSData?.cstOrigin === undefined) return false;
            if (!isSN && productICMSData?.cstTaxation === undefined) return false;
            if (isSN && productICMSData?.csosn === undefined) return false;

            return true;
        })();

        const isICMSDataCorrect = (() => {
            if (!isSN && productICMSData?.cstTaxation !== 51 && productICMSData?.cstTaxation !== 90 && (
                !productICMSData?.base?.calcBasisDetermination ||
                !productICMSData?.base?.taxRate
            )) {
                return false
            };

            return true;
        })();

        if (
            !isPrimaryDataCorrect ||
            !isICMSDataCorrect
        ) {
            showError('Erro', 'Preencha todos os campos obrigatórios');
            return;
        }

        const updatedProducts = products.map((p, i) => {
            if (i === selectedProductIndex) {
                return {
                    ...p,
                    icms: productICMSData,
                };
            }

            return p;
        });

        setProducts(updatedProducts);

        if (openNextProduct) {
            setProductData(products[selectedProductIndex + 1]);
            setProductICMSData(products[selectedProductIndex + 1] || {});
            setFormStep('general');
            setSelectedProductIndex(selectedProductIndex + 1);
            return;
        }

        setProductICMSData({});
        setProductData({});
        setSelectedProductIndex(null);
    }

    const getData = async () => {
        try {
            if (!id) {
                navigate(`/orders`);
                return;
            }

            const { data } = await api.get(`orders/${id}`);
    
            const { data: settingsData } = await api.get(`settings`);
    
            setSettings(settingsData);
            setProducts(data.items);
            setGeneralData({
                ...generalData,
                destinyCompany: data.client.commercialName,
            })
        } catch (error) {
            showError('Erro', 'Erro ao buscar dados.');
            navigate(`/orders`);
        }
    }

    const handleSaveSale = async () => {
        setSaving(true);

        try {
            const hasMissingProducts = products.forEach((product) => {
                if (!product.icms) return true;
            });

            if (hasMissingProducts) {
                showError('Erro', 'Preencha todos os produtos.');
                setSaving(false);
                return;
            }
            
            const body = {
                order: {
                    id
                },
                cfop: {
                    code: generalData.cfop
                },
                items: [],
                ...billingData,
            }

            body.items = products.map((product) => {
                const formattedProduct = {
                    ...product,
                    cfop: {
                        code: product?.cfop
                    },
                    icms: {
                        ...product?.icms,
                    },
                    ipi: {
                        ...product?.ipi,
                        cst: product?.pis?.cst?.number
                    },
                    pis: {
                        ...product?.pis,
                        cst: product?.pis?.cst?.number
                    },
                    cofins: {
                        ...product?.cofins,
                        cst: product?.cofins?.cst?.number
                    }
                }

                delete formattedProduct?.product?.description;
                delete formattedProduct?.product?.code;
                delete formattedProduct?.icms?.originSelect;
                delete formattedProduct?.icms?.cstSelect;
                delete formattedProduct?.icms?.csosnSelect;
                delete formattedProduct?.icms?.base?.calcBasisDeterminationSelect;
                delete formattedProduct?.icms?.st?.calcBasisDeterminationSelect;
                delete formattedProduct?.icms?.relief?.reasonSelect;

                return formattedProduct
            }, []);

            await api.post('movements/sales', body);

            showSuccess('Sucesso', 'Venda salva com sucesso');
            setSaving(false);
            navigate('/orders');
        } catch (error) {
            setSaving(false);
            showAxiosError(error);
        }
    }

    const handleSubmit = () => {
        confirmDialog({
            message: 'Deseja criar a venda desse pedido?',
            header: 'Atenção',
            icon: 'pi pi-info-circle',
            acceptLabel: 'Sim',
            rejectLabel: 'Não',
            accept: handleSaveSale
        })
    }

    const formContent = {
        'general':
            <div className="flex flex-column h-full">  
                <div className="flex-grow-1 overflow-y-scroll overflow-x-hidden	pr-4">
                    <ProductDetailsForm 
                        products={products}
                        productData={productData}
                        setProductData={setProductData}
                    />
                </div>
                <div className="w-full flex gap-4 bottom-0 bg-white py-3 flex-grow-0">
                    <Button 
                        className="w-full"
                        label="Configurar ICMS" 
                        loading={saving} 
                        onClick={() => handleProductDetailsChange(true)}
                    />

                    <Button 
                        className="w-full"
                        label="Salvar e fechar" 
                        outlined 
                        type="button" 
                        onClick={() => handleProductDetailsChange(false)} 
                    />
                </div>
            </div>,
        'icms':
            <div className="flex flex-column h-full">  
                <div className="flex-grow-1 overflow-y-scroll overflow-x-hidden	pr-4">
                    <ICMSForm
                        productData={productICMSData}
                        setProductData={setProductICMSData}
                        isSimpleNational={settings?.sn?.active}
                        totalValue={productData?.quantity * productData?.unitaryValue}
                        sn={settings?.sn}
                    />
                </div>
                <div className="w-full flex gap-4 bottom-0 bg-white py-3 flex-grow-0">
                    <Button 
                        className="w-full"
                        label="Salvar e fechar" 
                        type="button" 
                        onClick={() => handleProductICMSChange(false)} 
                    />

                    <Button 
                        className="w-full"
                        label="Próximo produto" 
                        outlined 
                        type="button" 
                        onClick={() => handleProductICMSChange(true)} 
                    />
                </div>
            </div>
    }

    useEffect(() => {      
        getData();
    }, []);

    const activeTabContent = {
        0: <GeneralInformation 
                generalData={generalData} 
                setGeneralData={setGeneralData} 
            />,
        1:  <ProductsList  
                products={products} 
                onProductClick={handleProductClick} 
            />,
        2:  <BillingInformation
                billingData={billingData}
                setBillingData={setBillingData}
            />,
    }

    return (
        <div>
            <PageTitle title="Nova venda"/>
            <TabMenu model={items} activeIndex={activeIndex} onTabChange={(e) => setActiveIndex(e.index)} />

            <PageContent className='mt-5'>
                {activeTabContent[activeIndex]}
            </PageContent>

            <div className="flex gap-4 mt-4">
                <Button 
                    label="Salvar" 
                    type="button" 
                    loading={saving}
                    onClick={() => handleSubmit()} 
                />

                <Button 
                    label="Cancelar" 
                    outlined 
                    type="button" 
                    disabled={saving}
                    onClick={() => {
                        navigate('/orders');
                    }} 
                />
            </div>

            <Sidebar 
                className="w-4 relative"
                position="right" 
                visible={selectedProductIndex !== null} 
                onHide={() => setSelectedProductIndex(null)}
            > 
                {formContent[formStep]}
            </Sidebar>
            <ConfirmDialog />
        </div>
    )
}

export default NewSale;